<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import AuthLayout from '@/layouts/AuthLayout.vue'
import BlankLayout from '@/layouts/BlankLayout.vue'
import DashboardLayout from '@/layouts/DashboardLayout.vue'
import ErrorBar from '@/components/generic/ErrorBar.vue'
import debug from '@/helpers/debug'
import useCookieConsent from '@/composables/useCookieConsent'
import { SET_APP_LOADING } from '@/store/mutation-types'
import { LANDING_PAGE } from '@/types'

const authResult = ref(null)

const store = useStore()
const route = useRoute()
const router = useRouter()
useCookieConsent()

const appLoading = computed(() => store.state.appLoading)
const callbackLandingPage = computed(() => store.state.auth.callbackLandingPage)
const layout = computed(() => {
  if (route.meta.layout === 'DashboardLayout') {
    return DashboardLayout
  }

  if (route.meta.layout === 'AuthLayout') {
    return AuthLayout
  }

  return BlankLayout
})

onBeforeMount(async () => {
  await router.isReady()

  if (import.meta.env.MODE === 'mockapi') {
    store.commit(SET_APP_LOADING, false)
    return
  }

  try {
    // Passwordless login redirects here with tokens in the URL hash.
    // Prefer parseHash — checkSession often fails on localhost because Auth0
    // always requires consent there and will not skip it silently.
    if (
      route.name === 'Callback' ||
      window.location.hash.includes('access_token')
    ) {
      const handled = await handleAuth0CallbackHash()
      if (handled) {
        return
      }
    }

    const authed = await checkAuth0Session()

    if (authed) {
      await fetchUserAndRedirect()
    }
  } catch (error) {
    debug('Error during App initial auth check', { cause: error })
  } finally {
    store.commit(SET_APP_LOADING, false)
  }
})

onMounted(() => {
  if (route.query.refid) {
    store.dispatch('setReferralLink', route.query.refid)
  }
})

const redirectToRoute = (name: string) => {
  try {
    router.replace({ name })
  } catch (error) {
    console.error('Error in App#redirectToRoute', { cause: error })
  }
}

const handleLoginRequiredError = (error: any) => {
  if (error?.code === 'login_required') {
    redirectToRoute('Login')
  }
}

const handleUserNotFound = (error: any) => {
  // 404: no donor account. 409: donor scheduled for deletion (POST /me).
  // Both land on the Whoops screen, which adapts its copy/actions to the state.
  const status = error?.response?.status
  if (status === 404 || status === 409) {
    redirectToRoute('DONOR_FLOW_ManageAccountUserNotFound')
  }
}

const handleAuth0CallbackHash = async (): Promise<boolean> => {
  try {
    // Belt-and-braces: restore intent from the callback query before GET /me.
    const authIntent = route.query.authintent
    if (authIntent === 'signup' || authIntent === 'login') {
      store.commit('SET_AUTH_INTENT', authIntent)
    }

    authResult.value = await store.dispatch('auth0ParseHash')
    await fetchUserAndRedirect()
    return true
  } catch (error) {
    debug('Error in App#handleAuth0CallbackHash', { cause: error })
    redirectToRoute('Login')
    return false
  }
}

const checkAuth0Session = async (): Promise<Boolean> => {
  try {
    authResult.value = await store.dispatch('auth0CheckSession')

    if (!authResult.value) {
      throw new Error('No result from Auth0')
    }

    return true
  } catch (error) {
    debug('Error in App#checkAuth0Session', { cause: error })

    const routeName = String(route.name || '')
    const authRouteNames = [
      'Login',
      'LoginMagicLink',
      'Signup',
      'SignupMagicLink',
    ]

    // Stay on public status screens instead of bouncing to login.
    if (
      routeName === 'AccountVerify' ||
      routeName === 'NotFound' ||
      authRouteNames.includes(routeName)
    ) {
      return false
    }
    handleLoginRequiredError(error)
    return false
  }
}

const fetchUserAndRedirect = async (): Promise<void> => {
  let landingPage
  try {
    await store.dispatch('storeToken', authResult.value)
    const user = await store.dispatch('fetchUser')
    landingPage = user.data.landingPage
    // Load Swiftaid Giving subscription state (free vs paid) from GET /me/plan.
    // Mock mode does this in the router guard; real mode resolves it here at boot.
    await store.dispatch('fetchDonorPlan')
  } catch (error) {
    debug('Error in App#fetchUserAndRedirect', { cause: error })
    handleUserNotFound(error)
    return
  }

  // Account verify and unknown URLs must stay put after a signed-in boot.
  if (route.name === 'AccountVerify' || route.name === 'NotFound') {
    return
  }

  // Leave users on whichever dashboard page they deep-linked to (Dashboard,
  // Donations, Statements, Account, Swiftaid status, …). Route guards handle
  // auth and sign-up state for these; don't bounce them to Manage account.
  if (route.meta.layout === 'DashboardLayout') {
    return
  }

  // Prevents people wanting to close account having
  // to add details and declarations before closing.
  // On a refresh of Close the stored value is already cleared, so check the route too.
  if (
    route.name === 'DONOR_FLOW_ManageAccountClose' ||
    callbackLandingPage.value === 'DONOR_FLOW_ManageAccountClose'
  ) {
    redirectToRoute('DONOR_FLOW_ManageAccountClose')
    return
  }

  // Send a user to the correct landing page
  // if not completed name & address
  if (landingPage === LANDING_PAGE.NAME_ADDRESS) {
    redirectToRoute('DONOR_FLOW_ManageAccountCreateDetails')
    return
  }
  // Send a user to the correct landing page
  // if not agreed to declaration
  if (landingPage === LANDING_PAGE.QUESTIONS) {
    redirectToRoute('DONOR_FLOW_ManageAccountCreateDeclaration')
    return
  }

  // Handle requested landing page
  if (callbackLandingPage.value) {
    redirectToRoute(callbackLandingPage.value)
  } else {
    redirectToRoute('Dashboard')
  }
}
</script>

<template>
  <ErrorBar />
  <div v-if="appLoading" class="sa-loader">
    <div class="sa-loader__ring"></div>
  </div>
  <div v-else>
    <transition name="fade" mode="out-in" appear>
      <component :is="layout" />
    </transition>
  </div>
</template>

<style lang="scss">
@import './sass/main';
</style>
